import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { useToast } from "@/components/ui/use-toast";
import {
  Activity,
  useChangeTodoCustomer,
  useListCustomers,
} from "@/lib/api/customer";

export type CustomerState = { id: string; name: string };

export function customerStateOf(todo: Activity | null): CustomerState {
  return { id: todo?.customer_id ?? "", name: todo?.Customer?.long_name ?? "" };
}

/** The todo with its customer replaced, for the parts of a modal that show the customer. */
export function withCustomer(todo: Activity | null, customer: CustomerState): Activity | null {
  return (
    todo && {
      ...todo,
      customer_id: customer.id,
      Customer: { ...todo.Customer, long_name: customer.name },
    }
  );
}

interface CustomerOption {
  value: string;
  label: string;
}

/** Same list as the Transcriptions customer filter; the API also requires a risk profile. */
export function useTodoCustomerOptions() {
  const customersResult = useListCustomers({ minimal: true, hasRiskProfile: true });
  const customers: { id: string; long_name: string }[] = customersResult.data?.data || [];
  const customerOptions: CustomerOption[] = customers
    .map((option) => ({ value: option.id, label: option.long_name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { customerOptions, isLoading: customersResult.isLoading };
}

/**
 * Returns a function that moves a todo to another customer and toasts the outcome.
 * It resolves to the saved customer, or null when the change failed.
 */
export function useSaveTodoCustomer(customerOptions: CustomerOption[]) {
  const { toast } = useToast();
  const changeTodoCustomer = useChangeTodoCustomer();

  return async (todoId: string, customerId: string): Promise<CustomerState | null> => {
    try {
      const result = await changeTodoCustomer({ todo_id: todoId, customer_id: customerId });

      if (!result.success) {
        toast({
          title: "Unable to change customer",
          description: result.message,
          variant: "destructive",
        });
        return null;
      }

      const name =
        result.data.entity.Customer?.long_name ??
        customerOptions.find((option) => option.value === customerId)?.label ??
        "";
      toast({
        title: "Customer changed",
        description: name ? `The Todo is now assigned to ${name}.` : "The Todo's customer was changed.",
      });
      return { id: result.data.entity.customer_id, name };
    } catch {
      toast({
        title: "Unable to change customer",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };
}

interface TodoCustomerPickerProps {
  id: string;
  testId: string;
  customerOptions: CustomerOption[];
  isLoading: boolean;
  /** The saved customer. */
  customerId: string;
  selectedCustomerId: string;
  onSelect: (customerId: string) => void;
  onSave: () => void;
  isSaving: boolean;
  /** Disables the controls while any other action is running. */
  isDisabled: boolean;
}

export function TodoCustomerPicker({
  id,
  testId,
  customerOptions,
  isLoading,
  customerId,
  selectedCustomerId,
  onSelect,
  onSave,
  isSaving,
  isDisabled,
}: TodoCustomerPickerProps) {
  const hasUnsavedCustomer = selectedCustomerId !== customerId;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
      <label htmlFor={id} className="text-sm font-medium">
        Customer
      </label>
      <Combobox
        id={id}
        dwimOnChange
        virtualizeList
        isModal
        options={customerOptions}
        // A non-risk customer (e.g. the unmatched fallback) isn't an option; show the placeholder.
        value={customerOptions.some((option) => option.value === selectedCustomerId) ? selectedCustomerId : ""}
        // Re-selecting the current option clears it; keep the selection instead.
        onValueChange={(value: string) => value && onSelect(value)}
        placeholder={isLoading ? "Loading customers..." : "Choose a customer"}
        disabled={isDisabled}
        className="w-[320px]"
        popoverClassName="min-w-[320px]"
      />
      {hasUnsavedCustomer && (
        <Button
          type="button"
          variant="outline"
          onClick={() => onSelect(customerId)}
          disabled={isDisabled}
        >
          Cancel
        </Button>
      )}
      <Button
        type="button"
        data-testid={testId}
        onClick={onSave}
        disabled={!hasUnsavedCustomer || !selectedCustomerId || isDisabled}
      >
        {isSaving ? "Saving..." : "Save Customer"}
      </Button>
    </div>
  );
}
